/**
 * InvoiceModal.jsx - one invoice on the Finance Invoice tab: new or edit
 * (number, project, invoice date, due date, amount, notes, the review flag),
 * its payments (record, correct, delete) and void. Every save goes to the
 * invoice ledger at once; the project's invoicing columns follow on the
 * server. A void invoice is shown read only.
 *
 *   invoiceId  the invoice to open; null for a new one
 *   projectId  a new invoice's project (from a deep link), if known
 *   projects   [{ id, quote_letter, customer, name, terms, ... }] for the picker
 *   editable   Finance Invoice Edit
 *   onChanged  after any save, so the lists behind re-read
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Msg, td, th, num } from '../spareParts/spUi';
import { Btn, Field, Grid, MUTED, NumInput, SelectInput, TextInput, fmtMoney } from '../quotes/quoteUi';
import { fmtDate } from '../../utils/format';
import {
  addPayment,
  createInvoice,
  deletePayment,
  errorOf,
  getInvoice,
  updateInvoice,
  updatePayment,
  voidInvoice,
} from './financeInvoicesApi';
import { STATUS_STYLE, netDaysOf, numberLabel, plusDays, projectHref, projectLabel } from './invoiceRules';

const METHODS = ['ACH', 'Wire', 'Check', 'Credit card', 'Other'];
const today = () => new Date().toLocaleDateString('en-CA');

export function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Open;
  return (
    <span
      data-testid="invoice-status"
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );
}

const formOf = (inv, projectId) => ({
  project_id: (inv && inv.project_id) || projectId || '',
  invoice_number: (inv && inv.invoice_number) || '',
  invoice_date: (inv && inv.invoice_date) || (inv ? '' : today()),
  due_date: (inv && inv.due_date) || '',
  amount: inv && inv.amount !== null && inv.amount !== undefined ? String(inv.amount) : '',
  notes: (inv && inv.notes) || '',
  needs_review: !!(inv && inv.needs_review),
  review_note: (inv && inv.review_note) || '',
});

export default function InvoiceModal({ invoiceId, projectId = null, projects = [], editable, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const [f, setF] = useState(() => formOf(null, projectId));
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ paid_on: today(), amount: '', method: 'ACH', reference: '', notes: '' });
  const [fixing, setFixing] = useState(null); // a payment being corrected: { id, paid_on, amount, ... }
  const [voiding, setVoiding] = useState(null); // the reason being typed
  const [dupOk, setDupOk] = useState(false);
  const isNew = !invoiceId && !inv;

  const load = useCallback(async (id) => {
    const res = await getInvoice(id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not load the invoice.') });
      return;
    }
    setInv(res.body);
    setF(formOf(res.body));
  }, []);
  useEffect(() => {
    if (invoiceId) load(invoiceId);
  }, [invoiceId, load]);

  const project = useMemo(() => projects.find((p) => String(p.id) === String(f.project_id)) || null, [projects, f.project_id]);
  const terms = project ? project.terms : inv && inv.terms;
  const netDays = netDaysOf(terms);
  // the due date a new invoice will get from the project's terms, when none is typed
  const dueHint = !f.due_date && f.invoice_date && netDays !== '' ? plusDays(f.invoice_date, netDays) : '';
  const locked = !editable || (inv && inv.status === 'Void');
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    const body = {
      invoice_number: f.invoice_number,
      invoice_date: f.invoice_date,
      due_date: f.due_date,
      amount: f.amount,
      notes: f.notes,
      needs_review: f.needs_review,
      review_note: f.review_note,
      ...(dupOk ? { allow_duplicate_number: true } : {}),
    };
    let res;
    if (isNew) res = await createInvoice({ ...body, project_id: f.project_id, needs_review: undefined, review_note: undefined });
    else res = await updateInvoice(inv.id, { ...body, project_id: f.project_id });
    setBusy(false);
    if (res.status === 409 && res.body && res.body.code === 'invoice_number_taken') {
      setMsg({ kind: 'error', text: `${res.body.error} Save again to keep the same number on two invoices.` });
      setDupOk(true);
      return;
    }
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not save the invoice.') });
      return;
    }
    setDupOk(false);
    const moved = res.body.moved;
    setMsg({
      kind: 'ok',
      text: moved
        ? `Saved. ${fmtMoney(moved.amount)} moved out of the opening balance (${fmtMoney(moved.left)} left there).`
        : isNew
          ? 'Invoice added to the ledger.'
          : 'Invoice saved.',
    });
    await load(res.body.id);
    if (onChanged) onChanged();
  };

  const recordPayment = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    const res = await addPayment(inv.id, pay);
    setBusy(false);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not record the payment.') });
      return;
    }
    setMsg({ kind: 'ok', text: `Payment of ${fmtMoney(res.body.amount)} recorded.` });
    setPay({ paid_on: today(), amount: '', method: pay.method, reference: '', notes: '' });
    await load(inv.id);
    if (onChanged) onChanged();
  };
  const saveFix = async () => {
    const res = await updatePayment(fixing.id, {
      paid_on: fixing.paid_on,
      amount: fixing.amount,
      method: fixing.method,
      reference: fixing.reference,
      notes: fixing.notes,
    });
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not change the payment.') });
      return;
    }
    setFixing(null);
    setMsg({ kind: 'ok', text: 'Payment changed.' });
    await load(inv.id);
    if (onChanged) onChanged();
  };
  const removePayment = async (p) => {
    if (!window.confirm(`Delete the payment of ${fmtMoney(p.amount)} received ${fmtDate(p.paid_on)}? Only for one entered by mistake.`))
      return;
    const res = await deletePayment(p.id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not delete the payment.') });
      return;
    }
    setMsg({ kind: 'ok', text: 'Payment deleted.' });
    await load(inv.id);
    if (onChanged) onChanged();
  };
  const doVoid = async () => {
    const res = await voidInvoice(inv.id, voiding);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not void the invoice.') });
      return;
    }
    setVoiding(null);
    setMsg({ kind: 'ok', text: 'Invoice voided. It stays in the ledger, out of every total.' });
    await load(inv.id);
    if (onChanged) onChanged();
  };

  const title = isNew ? 'New invoice' : inv ? `Invoice ${numberLabel(inv)}` : 'Invoice';
  const projectOptions = projects.map((p) => ({
    value: String(p.id),
    label: `${[p.customer, projectLabel({ ...p, project_name: p.name })].filter(Boolean).join(' - ')}`,
  }));

  return (
    <Modal open onClose={onClose} title={title} width={860}>
      <div style={{ fontFamily: 'Montserrat' }}>
        {inv && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
            <StatusChip status={inv.status} />
            <span>
              Amount <strong>{inv.amount === null ? '-' : fmtMoney(inv.amount)}</strong>
            </span>
            <span>
              Paid <strong>{fmtMoney(inv.paid)}</strong>
            </span>
            <span>
              Balance <strong>{inv.balance === null ? '-' : fmtMoney(inv.balance)}</strong>
            </span>
            {inv.days_past_due > 0 && <span style={{ color: '#B71C1C', fontWeight: 700 }}>{inv.days_past_due} days past due</span>}
            <a href={projectHref(inv)} style={{ marginLeft: 'auto', fontWeight: 700, color: '#1B5E20' }}>
              Open {projectLabel(inv)}
            </a>
          </div>
        )}
        {inv && inv.needs_review && (
          <div
            style={{
              padding: '8px 12px',
              background: '#FFF3E0',
              border: '1px solid #FFE0B2',
              borderRadius: 8,
              fontSize: 11.5,
              color: '#7A3E00',
              marginBottom: 12,
            }}
          >
            <strong>Needs review:</strong> {inv.review_note || 'Check this invoice and clear the flag.'} It stays out of receivables and
            aging until the flag is cleared.
          </div>
        )}
        {inv && inv.status === 'Void' && (
          <div style={{ padding: '8px 12px', background: '#F4F4F4', borderRadius: 8, fontSize: 11.5, color: '#444', marginBottom: 12 }}>
            Voided {fmtDate(inv.voided_at)}
            {inv.voided_by ? ` by ${inv.voided_by}` : ''}: {inv.void_reason}
          </div>
        )}
        <Msg msg={msg} />
        <Grid cols={4}>
          <Field label="Project" span={2}>
            {isNew ? (
              <SelectInput
                aria-label="Project"
                value={String(f.project_id)}
                onChange={set('project_id')}
                options={projectOptions}
                blank="Pick the project..."
                disabled={locked}
              />
            ) : (
              <SelectInput
                aria-label="Project"
                value={String(f.project_id)}
                onChange={set('project_id')}
                options={projectOptions}
                disabled={locked || (inv && inv.kind === 'opening')}
              />
            )}
          </Field>
          <Field label="Invoice #">
            <TextInput
              aria-label="Invoice number"
              value={f.invoice_number}
              onChange={set('invoice_number')}
              disabled={locked || (inv && inv.kind === 'opening')}
              placeholder={inv && inv.kind === 'opening' ? 'Opening balance' : 'e.g. 00617'}
            />
          </Field>
          <Field label="Amount ($)">
            <NumInput aria-label="Amount" value={f.amount} onChange={set('amount')} disabled={locked} placeholder="0.00" />
          </Field>
          <Field label="Invoice date">
            <TextInput aria-label="Invoice date" type="date" value={f.invoice_date} onChange={set('invoice_date')} disabled={locked} />
          </Field>
          <Field
            label="Due date"
            title={terms ? `Terms: ${terms}` : 'No terms on the project: due on the invoice date unless a date is typed'}
          >
            <TextInput aria-label="Due date" type="date" value={f.due_date} onChange={set('due_date')} disabled={locked} />
          </Field>
          <Field label="Terms">
            <div style={{ fontSize: 12, padding: '6px 0', color: MUTED }}>
              {terms || 'None on the project'}
              {dueHint ? ` · due ${fmtDate(dueHint)}` : ''}
            </div>
          </Field>
          <Field label="Needs review">
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '6px 0' }}>
              <input
                type="checkbox"
                aria-label="Needs review"
                checked={f.needs_review}
                disabled={locked || isNew}
                onChange={(e) => set('needs_review')(e.target.checked)}
              />
              {f.needs_review ? 'Out of aging until cleared' : 'Reviewed'}
            </label>
          </Field>
          <Field label="Notes" span={4}>
            <TextInput
              aria-label="Notes"
              value={f.notes}
              onChange={set('notes')}
              disabled={locked}
              placeholder="Anything accounting should know"
            />
          </Field>
        </Grid>
        {!locked && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Btn primary onClick={save} disabled={busy || (isNew && !f.project_id)}>
              {isNew ? 'Add invoice' : 'Save invoice'}
            </Btn>
            {inv && inv.status !== 'Void' && voiding === null && (
              <Btn
                danger
                onClick={() => setVoiding('')}
                title={inv.payment_count ? 'Delete its payments first' : 'Void keeps the invoice, out of every total'}
              >
                Void...
              </Btn>
            )}
          </div>
        )}
        {voiding !== null && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
            <Field label="Why is it void?" span={1}>
              <TextInput
                aria-label="Void reason"
                value={voiding}
                onChange={setVoiding}
                placeholder="e.g. billed to the wrong PO"
                style={{ minWidth: 320 }}
              />
            </Field>
            <Btn danger onClick={doVoid} disabled={!voiding.trim()}>
              Void invoice
            </Btn>
            <Btn onClick={() => setVoiding(null)}>Cancel</Btn>
          </div>
        )}

        {inv && (
          <div style={{ marginTop: 18 }}>
            <div
              style={{ fontSize: 10.5, fontWeight: 800, color: '#1B5E20', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}
            >
              Payments
            </div>
            {inv.payments.length === 0 ? (
              <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 8 }}>No payments recorded yet.</div>
            ) : (
              <table aria-label="Payments on this invoice" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={th}>Received</th>
                    <th style={th}>Method</th>
                    <th style={th}>Reference</th>
                    <th style={th}>Notes</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {inv.payments.map((p) =>
                    fixing && fixing.id === p.id ? (
                      <tr key={p.id}>
                        <td style={td}>
                          <TextInput
                            type="date"
                            aria-label="Payment date"
                            value={fixing.paid_on}
                            onChange={(v) => setFixing({ ...fixing, paid_on: v })}
                          />
                        </td>
                        <td style={td}>
                          <SelectInput
                            value={fixing.method || ''}
                            onChange={(v) => setFixing({ ...fixing, method: v })}
                            options={METHODS}
                            blank="-"
                          />
                        </td>
                        <td style={td}>
                          <TextInput value={fixing.reference || ''} onChange={(v) => setFixing({ ...fixing, reference: v })} />
                        </td>
                        <td style={td}>
                          <TextInput value={fixing.notes || ''} onChange={(v) => setFixing({ ...fixing, notes: v })} />
                        </td>
                        <td style={num}>
                          <NumInput
                            aria-label="Payment amount"
                            value={String(fixing.amount)}
                            onChange={(v) => setFixing({ ...fixing, amount: v })}
                          />
                        </td>
                        <td style={td}>
                          <Btn small primary onClick={saveFix}>
                            Save
                          </Btn>{' '}
                          <Btn small onClick={() => setFixing(null)}>
                            Cancel
                          </Btn>
                        </td>
                      </tr>
                    ) : (
                      <tr key={p.id}>
                        <td style={td}>{fmtDate(p.paid_on)}</td>
                        <td style={td}>{p.method || '-'}</td>
                        <td style={td}>{p.reference || '-'}</td>
                        <td style={td}>{p.notes || ''}</td>
                        <td style={num}>{fmtMoney(p.amount)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {editable && (
                            <>
                              <Btn small onClick={() => setFixing({ ...p })}>
                                Change
                              </Btn>{' '}
                              <Btn small danger onClick={() => removePayment(p)}>
                                Delete
                              </Btn>
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
            {editable && inv.status !== 'Void' && inv.amount !== null && inv.balance > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  padding: '10px 12px',
                  background: '#F6F9F3',
                  border: '1px solid #E0EAD8',
                  borderRadius: 8,
                }}
              >
                <Field label="Received">
                  <TextInput type="date" aria-label="Received on" value={pay.paid_on} onChange={(v) => setPay({ ...pay, paid_on: v })} />
                </Field>
                <Field label="Amount ($)">
                  <NumInput
                    aria-label="Payment amount"
                    value={pay.amount}
                    onChange={(v) => setPay({ ...pay, amount: v })}
                    placeholder={String(inv.balance)}
                  />
                </Field>
                <Field label="Method">
                  <SelectInput
                    aria-label="Payment method"
                    value={pay.method}
                    onChange={(v) => setPay({ ...pay, method: v })}
                    options={METHODS}
                  />
                </Field>
                <Field label="Reference">
                  <TextInput
                    aria-label="Payment reference"
                    value={pay.reference}
                    onChange={(v) => setPay({ ...pay, reference: v })}
                    placeholder="Check #, remittance..."
                  />
                </Field>
                <Btn small onClick={() => setPay({ ...pay, amount: String(inv.balance) })}>
                  Full balance
                </Btn>
                <Btn primary onClick={recordPayment} disabled={busy || !pay.amount}>
                  Record payment
                </Btn>
              </div>
            )}
            <div style={{ fontSize: 10, color: MUTED, marginTop: 10 }}>
              Entered by {inv.created_by || 'someone'}
              {inv.created_at ? ` on ${fmtDate(inv.created_at)}` : ''}
              {inv.updated_by ? ` · last changed by ${inv.updated_by}${inv.updated_at ? ` on ${fmtDate(inv.updated_at)}` : ''}` : ''}
              {inv.source === 'backfill'
                ? ' · carried over from the project before the ledger'
                : inv.source === 'project'
                  ? ' · added on the project'
                  : ''}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
